import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useSeo from "@/lib/useSeo";
import { motion } from "framer-motion";
import { Download, Check } from "lucide-react";
import {
  LineChart,
  Line,
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";

const BAND_COLOUR = {
  Aligned: "#E86A20",
  Divergent: "#3B4A6B",
  Disconnected: "#B23A1A",
};

export default function AnnualIndex() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useSeo({
    title: "The Value Realisation Index™ — Public Cohort",
    description:
      "An anonymised monthly cohort snapshot of The Value Realisation Index™: median VRI, band distribution, and directorate movement across every organisation using the diagnostic.",
    path: "/annual-index",
    ogImage: `${API}/public/annual-index-card.png`,
  });

  useEffect(() => {
    api
      .get("/public/cohort")
      .then(({ data }) => setData(data))
      .catch((e) => setError(e.response?.data?.detail || e.message));
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-linen">
        <Navbar />
        <div className="max-w-4xl mx-auto p-12 text-heritage" data-testid="annual-index-error">
          {error}
        </div>
        <Footer />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-linen">
        <Navbar />
        <div className="max-w-4xl mx-auto p-12 tick-label">Loading…</div>
        <Footer />
      </div>
    );
  }

  if (!data.published) {
    return <Unpublished data={data} />;
  }

  const { cohort_size, current_state, weekly_aggregate, stories, published_at } = data;
  const monthLabel = formatMonth(published_at);

  return (
    <div className="min-h-screen bg-linen" data-testid="annual-index">
      <Navbar />

      {/* Editorial masthead */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 pb-8">
        <div className="tick-label-accent mb-4">The Annual Index™ · Public Cohort</div>
        <h1 className="display-serif text-4xl md:text-6xl lg:text-7xl leading-none" data-testid="annual-index-title">
          The state of value realisation.
        </h1>
        <div className="rule-orange my-8" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <p className="text-lg text-charcoal leading-relaxed max-w-2xl">
            An anonymised monthly read of every organisation using The Value Realisation Index™.
            No names. No sectors. Just the shape of the conversation happening inside boardrooms —
            how aligned they are, where they're drifting, and who is quietly closing the gap.
          </p>
          <div className="tick-label">
            <div>Published {monthLabel}</div>
            <div className="mt-1 text-charcoal">
              {cohort_size.organisations} organisations · {cohort_size.directorates} directorates ·{" "}
              {cohort_size.snapshots} weekly snapshots
            </div>
          </div>
        </div>
      </section>

      {/* Big number — cohort median */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-oxford/10">
          <BigCell
            label="Cohort Median VRI"
            value={current_state.median_blended_vri ?? "—"}
            sub={`across ${current_state.directorates_measured} directorates`}
            accent
            testid="cohort-median-vri"
          />
          <BigCell
            label="Aligned"
            value={current_state.band_distribution.Aligned}
            sub="directorates moving as one"
            testid="cohort-aligned"
          />
          <BigCell
            label="Disconnected"
            value={current_state.band_distribution.Disconnected}
            sub="where value is quietly leaking"
            testid="cohort-disconnected"
            colour="#B23A1A"
          />
        </div>
      </section>

      {/* Weekly line chart */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <div className="tick-label-accent mb-6">Median VRI, week by week</div>
        <div className="border border-oxford/15 bg-linen p-6" data-testid="annual-index-line">
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={weekly_aggregate.map((w) => ({
              week: w.week.replace(/^\d{4}-/, ""),
              vri: w.median_blended_vri,
              n: w.n,
            }))} margin={{ top: 8, right: 24, left: 0, bottom: 8 }}>
              <CartesianGrid stroke="#0E2148" strokeOpacity={0.08} vertical={false} />
              <XAxis dataKey="week" tick={{ fill: "#0E2148", fontSize: 11 }} />
              <YAxis domain={[0, 100]} tick={{ fill: "#0E2148", fontSize: 10 }} />
              <Tooltip
                contentStyle={{ background: "#FAF5EA", border: "1px solid #0E214830", fontSize: 12 }}
              />
              <Line
                type="monotone"
                name="Median VRI"
                dataKey="vri"
                stroke="#E86A20"
                strokeWidth={2.5}
                dot={{ r: 4, stroke: "#E86A20", strokeWidth: 2, fill: "#F4EEDC" }}
                activeDot={{ r: 6, fill: "#E86A20" }}
                isAnimationActive={false}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Band distribution bar */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        <div className="tick-label-accent mb-6">Band mix, week by week</div>
        <div className="border border-oxford/15 bg-linen p-6" data-testid="annual-index-bands">
          <div className="flex items-end gap-2 h-40">
            {weekly_aggregate.map((w) => {
              const total =
                (w.labels.Aligned || 0) + (w.labels.Divergent || 0) + (w.labels.Disconnected || 0) || 1;
              const alignedPct = ((w.labels.Aligned || 0) / total) * 100;
              const divergentPct = ((w.labels.Divergent || 0) / total) * 100;
              const disconnectedPct = ((w.labels.Disconnected || 0) / total) * 100;
              return (
                <div
                  key={w.week}
                  className="flex-1 min-w-0 flex flex-col items-center gap-1 h-full"
                >
                  <div
                    title={`${w.week} · n=${w.n}`}
                    className="w-full flex flex-col justify-end flex-1"
                  >
                    <div style={{ background: BAND_COLOUR.Disconnected, height: `${disconnectedPct}%` }} />
                    <div style={{ background: BAND_COLOUR.Divergent, height: `${divergentPct}%` }} />
                    <div style={{ background: BAND_COLOUR.Aligned, height: `${alignedPct}%` }} />
                  </div>
                  <div className="tick-label text-[10px] mt-2 text-center w-full truncate">
                    {w.week.replace(/^\d{4}-/, "")}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-6 mt-6 text-sm border-t border-oxford/10 pt-4">
            <Legend colour={BAND_COLOUR.Aligned} label="Aligned" />
            <Legend colour={BAND_COLOUR.Divergent} label="Divergent" />
            <Legend colour={BAND_COLOUR.Disconnected} label="Disconnected" />
          </div>
        </div>
      </section>

      {/* Anonymised stories */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="tick-label-accent mb-6">The month's story</div>

        {stories.narrative?.text && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="border-l-2 border-orange bg-parchment pl-8 pr-6 py-6 mb-10"
            data-testid="story-narrative"
          >
            <p className="display-serif italic text-2xl md:text-3xl text-oxford leading-snug">
              {stories.narrative.text}
            </p>
            <div className="tick-label mt-4 text-charcoal">
              {stories.narrative.fallback_to_mechanical
                ? "Editorial summary"
                : `Drafted by ${stories.narrative.model || "Claude Sonnet 5"} from the anonymised cohort data`}
            </div>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {stories.biggest_gainer && (
            <StoryCard
              kind="gainer"
              story={stories.biggest_gainer}
              testid="story-gainer"
            />
          )}
          {stories.biggest_drifter && (
            <StoryCard
              kind="drifter"
              story={stories.biggest_drifter}
              testid="story-drifter"
            />
          )}
          {!stories.biggest_gainer && !stories.biggest_drifter && !stories.narrative?.text && (
            <div className="text-charcoal">Not enough movement this month to tell a story.</div>
          )}
        </div>
      </section>

      {/* Share card + newsletter subscribe */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ShareCard month={monthLabel} />
          <NewsletterSubscribe />
        </div>
      </section>

      {/* Manifesto footer */}
      <section className="border-t border-oxford/10 bg-parchment">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-20 text-center">
          <div className="tick-label-accent mb-4">Publish your own read</div>
          <h2 className="display-serif text-3xl md:text-5xl leading-tight">
            Your directorate could be in the <em className="italic text-orange">next</em> edition.
          </h2>
          <p className="mt-6 text-charcoal max-w-2xl mx-auto">
            The Value Realisation Index™ is a twenty-minute diagnostic. If your board would like to
            appear — anonymously — in the next monthly cohort, start here.
          </p>
          <Link to="/diagnostic" className="btn-primary mt-8 inline-flex" data-testid="annual-cta">
            Take the Index →
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Unpublished({ data }) {
  return (
    <div className="min-h-screen bg-linen" data-testid="annual-index-unpublished">
      <Navbar />
      <section className="max-w-3xl mx-auto px-6 lg:px-12 py-24 text-center">
        <div className="tick-label-accent mb-4">The Annual Index™</div>
        <h1 className="display-serif text-4xl md:text-6xl leading-none">
          The cohort is still forming.
        </h1>
        <div className="rule-orange my-8 mx-auto" />
        <p className="text-lg text-charcoal">
          We publish the anonymised cohort read only once enough organisations have taken part —
          currently {data.current_directorates ?? 0} of the minimum{" "}
          {data.min_directorates_required} directorates.
        </p>
        <p className="mt-4 text-charcoal">
          The threshold is a privacy floor: below it, the cohort is small enough to be identifying.
        </p>
        <Link to="/diagnostic" className="btn-primary mt-10 inline-flex">
          Take the Index →
        </Link>
      </section>
      <Footer />
    </div>
  );
}

function BigCell({ label, value, sub, accent, colour, testid }) {
  return (
    <div className="bg-linen p-10" data-testid={testid}>
      <div className={accent ? "tick-label-accent" : "tick-label"}>{label}</div>
      <div
        className="mt-4 font-mono text-6xl md:text-7xl leading-none"
        style={{ color: colour || "#0E2148" }}
      >
        {value}
      </div>
      {sub && <div className="mt-2 tick-label">{sub}</div>}
    </div>
  );
}

function StoryCard({ kind, story, testid }) {
  const isGainer = kind === "gainer";
  const arrow = isGainer ? "▲" : "▼";
  const accentColour = isGainer ? "#E86A20" : "#B23A1A";
  const headline = isGainer ? "Closed the widest gap" : "Drifted the furthest";
  const verb = isGainer ? "moved up" : "moved down";
  const bandChanged = story.from_band !== story.to_band;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="border border-oxford/15 bg-linen p-8"
      data-testid={testid}
    >
      <div className="tick-label-accent" style={{ color: accentColour }}>
        {headline}
      </div>
      <div className="mt-3 display-serif text-3xl md:text-4xl text-oxford">
        {story.role_bucket}
      </div>
      <div className="tick-label mt-2">
        {story.weeks} week{story.weeks === 1 ? "" : "s"}
      </div>
      <div className="rule my-6" />
      <div className="flex items-baseline gap-6">
        <div>
          <div className="tick-label">Δ VRI</div>
          <div className="font-mono text-4xl mt-1" style={{ color: accentColour }}>
            {arrow} {Math.abs(story.delta_vri)}
          </div>
        </div>
        {bandChanged && (
          <div>
            <div className="tick-label">Band</div>
            <div className="mt-1 font-mono text-sm">
              <span style={{ color: BAND_COLOUR[story.from_band] || "#0E2148" }}>{story.from_band}</span>
              <span className="text-oxford/40 mx-2">→</span>
              <span style={{ color: BAND_COLOUR[story.to_band] || "#0E2148" }}>{story.to_band}</span>
            </div>
          </div>
        )}
      </div>
      <p className="mt-6 text-charcoal">
        {story.role_bucket} {verb} {Math.abs(story.delta_vri)} points on the Value Realisation Index —
        {bandChanged
          ? ` moving their directorate from ${story.from_band} to ${story.to_band}.`
          : ` without leaving the ${story.to_band} band.`}
      </p>
    </motion.div>
  );
}

function Legend({ colour, label }) {
  return (
    <div className="flex items-center gap-2">
      <span className="inline-block w-3 h-3" style={{ background: colour }} />
      <span className="text-oxford">{label}</span>
    </div>
  );
}

function formatMonth(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-").map(Number);
  const months = ["January", "February", "March", "April", "May", "June",
                  "July", "August", "September", "October", "November", "December"];
  if (!y || !m || m < 1 || m > 12) return ym;
  return `${months[m - 1]} ${y}`;
}

function ShareCard({ month }) {
  const cardUrl = `${API}/public/annual-index-card.png`;
  return (
    <div className="border border-oxford/15 bg-linen p-8" data-testid="share-card">
      <div className="tick-label-accent mb-4">Share this month</div>
      <div className="display-serif text-2xl text-oxford mb-4">
        The Annual Index™ · {month}
      </div>
      <div className="border border-oxford/10 bg-parchment p-2 mb-6 overflow-hidden">
        <img
          src={cardUrl}
          alt={`Annual Index cohort card for ${month}`}
          className="w-full h-auto block"
          loading="lazy"
        />
      </div>
      <a
        href={cardUrl}
        download={`annual-index-${month.replace(/\s+/g, "-").toLowerCase()}.png`}
        className="btn-ghost"
        data-testid="share-card-download"
      >
        <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
        Download image (1200×630)
      </a>
      <p className="mt-4 text-xs text-charcoal">
        Ready-made for LinkedIn. The image regenerates each month from the anonymised cohort.
      </p>
    </div>
  );
}

function NewsletterSubscribe() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setMessage("");
    try {
      const { data } = await api.post("/newsletter/subscribe", { email, name: name || undefined });
      setStatus("success");
      if (data.status === "already_subscribed") {
        setMessage("You're already on the list — nothing more to do.");
      } else if (data.status === "resubscribed") {
        setMessage("Welcome back. You'll receive next month's edition.");
      } else {
        setMessage("Thank you. The next monthly cohort will land in your inbox.");
      }
      setEmail("");
      setName("");
    } catch (err) {
      setStatus("error");
      setMessage(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="border border-oxford/15 bg-linen p-8" data-testid="newsletter-subscribe">
      <div className="tick-label-accent mb-4">A monthly rhythm</div>
      <div className="display-serif text-2xl text-oxford mb-3">
        Land next month's cohort in your inbox.
      </div>
      <p className="text-charcoal mb-6">
        One email a month, on the first of the month. The anonymised read of every board's
        directorate — nothing else. Unsubscribe with a single click.
      </p>
      <form onSubmit={submit} className="space-y-3">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name (optional)"
          className="w-full border border-oxford/20 px-3 py-3 bg-parchment focus:outline-none focus:border-oxford"
          data-testid="newsletter-name"
        />
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          className="w-full border border-oxford/20 px-3 py-3 bg-parchment focus:outline-none focus:border-oxford"
          data-testid="newsletter-email"
        />
        <button
          type="submit"
          disabled={status === "loading"}
          className="btn-primary w-full justify-center"
          data-testid="newsletter-submit"
        >
          {status === "loading" ? "Subscribing…" : status === "success" ? (<><Check className="w-3.5 h-3.5" strokeWidth={1.5}/> Subscribed</>) : "Subscribe"}
        </button>
      </form>
      {message && (
        <div
          className={`mt-4 text-sm ${status === "error" ? "text-heritage" : "text-orange"}`}
          data-testid="newsletter-message"
        >
          {message}
        </div>
      )}
    </div>
  );
}

